import { Fragment } from "react";
import type { ReactNode } from "react";
import type { Content } from "../content/model";
import { REGISTRY } from "./registry";
import { resolveProps, shows } from "./spec";
import type { SpecNode, ViewSpec } from "./spec";

// THE RENDERER: (spec, content) -> React.
//
// It knows about specs, components and bindings. It knows nothing whatever about units, lessons,
// standards, rubrics or teachers. There is no `if (entityType === "lesson")` in this file and
// there must never be one: the moment the renderer knows what it is drawing, every new kind of
// thing needs a change here, which is the situation the whole arrangement exists to end. Behaviour
// that a screen needs goes in a component, or in the spec as a `$when`.
//
// HOST SLOTS are the one place the outside world gets in. A spec may say WHERE student work, a
// rubric or a control belongs, by name; the screen passes what goes there. Specs bind to
// curriculum content only — a spec that could reach a student's score would be a spec that has to
// be trusted with one, and the producer of a spec will one day be a model.
//
// KEYS. Children come from the spec, which is cached and identical between renders, so a node's
// position is a stable identity. Lists INSIDE a component are keyed by the component on something
// from the content — a standard's code, a block's position in a fixed array — because a list keyed
// by a changing index remounts its subtree on every change, which loses focus in an input and
// makes a repaint look like a bug.

export type HostSlots = Record<string, ReactNode>;

export function renderSpec(spec: ViewSpec, content: Content, slots: HostSlots = {}): ReactNode {
  return <Node node={spec.root} content={content} slots={slots} />;
}

function Node({ node, content, slots }: { node: SpecNode; content: Content; slots: HostSlots }) {
  if (!shows(node, content)) return null;

  if (node.component === "HostSlot") {
    const name = String(node.props?.slot ?? "");
    return <Fragment>{slots[name] ?? null}</Fragment>;
  }

  const component = REGISTRY[node.component];
  if (!component) {
    // Validation refuses this before rendering, so reaching here means something rendered a spec
    // without validating it. Say so rather than throwing: one unknown node should not blank a
    // teacher's screen.
    console.error(`[spec] no component called "${node.component}" — the spec was not validated`);
    return null;
  }

  const children = (node.children ?? []).map((child, i) => (
    <Node key={i} node={child} content={content} slots={slots} />
  ));

  return (
    <Fragment>
      {component.render(resolveProps(node.props, content),
                        children.length > 0 ? children : null)}
    </Fragment>
  );
}
